import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import BitcoinIcon from "@/components/icons/BitcoinIcon";
import BnbIcon from "@/components/icons/BnbIcon";

const HeroSection = () => {
  const [prices, setPrices] = useState<{ btc: number; bnb: number; btcChange: number; bnbChange: number } | null>(null);

  const scrollToPlans = () => {
    document.getElementById("plans")?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const res = await fetch(
          "https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,binancecoin&vs_currencies=usd&include_24hr_change=true"
        );
        const data = await res.json();
        setPrices({
          btc: data.bitcoin.usd,
          bnb: data.binancecoin.usd,
          btcChange: data.bitcoin.usd_24h_change,
          bnbChange: data.binancecoin.usd_24h_change,
        });
      } catch (e) {
        console.error("Price fetch failed", e);
      }
    };
    const scheduleInitialFetch = () => {
      // Delay fetch well past LCP to avoid extending the critical request chain
      setTimeout(() => {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => fetchPrices());
        } else {
          fetchPrices();
        }
      }, 3000);
    };
    scheduleInitialFetch();
    const interval = setInterval(fetchPrices, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative flex min-h-screen items-center justify-center overflow-hidden pt-16">
      {/* Background effects */}
      <div className="absolute inset-0">
        <div className="absolute left-1/4 top-1/4 h-96 w-96 rounded-full bg-gold/5 blur-3xl animate-pulse-glow" />
        <div className="absolute bottom-1/4 right-1/4 h-96 w-96 rounded-full bg-gold/3 blur-3xl animate-pulse-glow" style={{ animationDelay: "1.5s" }} />
      </div>

      {/* Floating icons */}
      <div className="absolute left-[10%] top-[20%] animate-float opacity-20">
        <BitcoinIcon size={120} />
      </div>
      <div className="absolute right-[10%] top-[30%] animate-float-delayed opacity-20">
        <BnbIcon size={100} />
      </div>
      <div className="absolute bottom-[20%] left-[20%] animate-spin-slow opacity-10">
        <BitcoinIcon size={80} />
      </div>
      <div className="absolute bottom-[15%] right-[15%] animate-float opacity-10">
        <BnbIcon size={60} />
      </div>

      <div className="container relative z-10 mx-auto px-4 text-center">
        <div className="mx-auto flex items-center justify-center gap-4 mb-8 animate-[fadeSlideUp_0.8s_ease-out_both]">
          <BitcoinIcon size={72} className="drop-shadow-[0_0_20px_rgba(255,215,0,0.5)]" />
          <BnbIcon size={72} className="drop-shadow-[0_0_20px_rgba(255,215,0,0.5)]" />
        </div>

        <h1 className="font-display text-4xl font-bold leading-tight sm:text-5xl md:text-7xl">
          <span className="gradient-gold-text text-glow-gold">Smart BTC & BNB</span>
          <br />
          <span className="text-foreground">Asset GROWTH</span>
        </h1>

        <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground font-body md:text-xl animate-[fadeSlideUp_0.8s_ease-out_0.4s_both]">
          Join the 10x 7-Day Plan. Trading Cycle powered by strategic crypto market analysis.
        </p>

        <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center animate-[fadeSlideUp_0.8s_ease-out_0.6s_both]">
          <Link
            to="/register"
            className="gradient-gold rounded-lg px-8 py-3 font-display text-sm font-semibold text-primary-foreground transition-all hover:scale-105 hover:shadow-[0_0_30px_rgba(255,215,0,0.4)] glow-gold"
          >
            Start Investing
          </Link>
          <button
            onClick={scrollToPlans}
            className="rounded-lg border border-gold-dark px-8 py-3 font-display text-sm font-semibold text-gold transition-all hover:bg-gold/10"
          >
            View Plans
          </button>
        </div>

        {prices && (
          <div className="mt-12 flex flex-wrap items-center justify-center gap-6 animate-[fadeSlideUp_0.8s_ease-out_0.8s_both]">
            <div className="flex items-center gap-3 rounded-lg border border-border bg-card/60 px-5 py-3 backdrop-blur-sm">
              <BitcoinIcon size={28} />
              <div className="text-left">
                <p className="font-display text-xs text-muted-foreground">BTC/USD</p>
                <p className="font-display text-lg font-bold text-foreground">
                  ${prices.btc.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </p>
              </div>
              <span className={`flex items-center gap-1 text-xs font-semibold ${prices.btcChange >= 0 ? "text-green-400" : "text-destructive"}`}>
                {prices.btcChange >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                {Math.abs(prices.btcChange).toFixed(2)}%
              </span>
            </div>
            <div className="flex items-center gap-3 rounded-lg border border-border bg-card/60 px-5 py-3 backdrop-blur-sm">
              <BnbIcon size={28} />
              <div className="text-left">
                <p className="font-display text-xs text-muted-foreground">BNB/USD</p>
                <p className="font-display text-lg font-bold text-foreground">
                  ${prices.bnb.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </p>
              </div>
              <span className={`flex items-center gap-1 text-xs font-semibold ${prices.bnbChange >= 0 ? "text-green-400" : "text-destructive"}`}>
                {prices.bnbChange >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                {Math.abs(prices.bnbChange).toFixed(2)}%
              </span>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default HeroSection;
